import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Compass, Loader2, Trash2, FileText, Target, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const CareerClarityReports = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<any | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      fetchReports();
    }
  }, [user, authLoading]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("career_match_reports")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (id: string) => {
    try {
      const { error } = await supabase
        .from("career_match_reports")
        .delete()
        .eq("id", id);

      if (error) throw error;
      setReports(reports.filter((r) => r.id !== id));
      toast.success("Report deleted");
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Failed to delete report");
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === "completed") return <Badge className="bg-green-500 text-white">Completed</Badge>;
    if (status === "processing") return <Badge className="bg-yellow-500 text-white">Processing</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };

  const getRoleMatches = (report: any): any[] => {
    if (Array.isArray(report.role_matches)) return report.role_matches;
    return [];
  };

  if (authLoading || loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/career-clarity-report")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Career Clarity
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Career Clarity Reports</h1>
              <p className="text-muted-foreground mt-1">View all your career match analysis results</p>
            </div>
          </div>
          <Button onClick={() => navigate("/career-clarity-report")}>
            <Compass className="h-4 w-4 mr-2" />
            New Analysis
          </Button>
        </div>

        {reports.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Compass className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Reports Yet</h3>
              <p className="text-muted-foreground mb-4 text-center">Start a career clarity analysis to see your reports here.</p>
              <Button onClick={() => navigate("/career-clarity-report")}>Start Career Analysis</Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Your Career Reports ({reports.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Report Name</TableHead>
                    <TableHead>Matches Found</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell className="font-medium">{report.report_name || "Career Analysis"}</TableCell>
                      <TableCell>
                        <Badge variant="outline"><Target className="h-3 w-3 mr-1" />{getRoleMatches(report).length} roles</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(report.status)}</TableCell>
                      <TableCell>{format(new Date(report.created_at), "MMM d, yyyy")}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedReport(report); setIsDialogOpen(true); }}>
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteReport(report.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Compass className="h-5 w-5" />
                Career Clarity Report
              </DialogTitle>
            </DialogHeader>
            {selectedReport && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-muted-foreground">Report Name</p><p className="font-medium">{selectedReport.report_name || "Career Analysis"}</p></div>
                  <div><p className="text-sm text-muted-foreground">Status</p>{getStatusBadge(selectedReport.status)}</div>
                </div>
                <div>
                  <h4 className="font-semibold mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4" />Role Matches</h4>
                  <div className="grid gap-4">
                    {getRoleMatches(selectedReport).map((role: any, idx: number) => (
                      <Card key={idx}>
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h5 className="font-semibold text-lg">{role.title}</h5>
                              {role.description && <p className="text-muted-foreground text-sm mt-1">{role.description}</p>}
                            </div>
                            <div className="px-4 py-2 rounded-full font-bold bg-primary/10 text-primary">{role.fit_score}%</div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default CareerClarityReports;
